<template>
  <div class="work-calendar">
    <div class="studio-cockpit-wrapper fill-height d-flex flex-column" :class="themeClasses" @dragend="onDragEndGlobal" @mouseup="onDragEndGlobal">
      <!-- Week View -->
      <work-calendar-week
        v-if="viewMode === 'week'"
        :initial-date="currentDate"
        @back="$emit('back')"
        @switch-view="viewMode = 'day'"
        @open-day="openDayFromWeek" />

      <!-- Day View (existing) -->
      <template v-if="viewMode === 'day'">
        <!-- Premium Header -->
        <div class="wc-header" :class="$vuetify.breakpoint.smAndDown ? 'wc-header-mobile' : ''">
          <!-- Row 1 on mobile: date + back btn -->
          <div class="wc-header-left">
            <v-btn icon x-large dark class="wc-back-btn mr-3" @click="$router.push('/dashboard/shift-management')">
              <v-icon x-large>mdi-arrow-left</v-icon>
            </v-btn>
            <div class="wc-date-block">
              <div class="wc-day-name">{{ $moment(currentDate).format("dddd") }}</div>
              <div class="wc-full-date">{{ $moment(currentDate).format("DD. MMMM YYYY") }}</div>
            </div>
            <div v-if="isToday" class="wc-today-badge ml-2">Heute</div>
          </div>

          <!-- Row 2 on mobile / inline on desktop: nav controls -->
          <div class="wc-header-controls">
            <div class="wc-header-right">
              <div class="wc-nav-group">
                <v-btn icon @click="changeDate(-1)" class="wc-nav-btn"><v-icon>mdi-chevron-left</v-icon></v-btn>
                <v-btn text class="wc-today-btn" :class="{ 'is-today': isToday }" @click="jumpToToday">Heute</v-btn>
                <v-btn icon @click="changeDate(1)" class="wc-nav-btn"><v-icon>mdi-chevron-right</v-icon></v-btn>
              </div>
              <v-menu offset-y :close-on-content-click="false" transition="scale-transition">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on" class="wc-icon-btn"><v-icon>mdi-calendar-search</v-icon></v-btn>
                </template>
                <v-date-picker
                  style="z-index: 9999 !important"
                  v-model="currentDate"
                  no-title
                  scrollable
                  :dark="isDarkMode"
                  color="green accent-3"
                  @input="fetchData"></v-date-picker>
              </v-menu>

              <v-btn depressed @click="viewMode = 'week'" class="wc-view-toggle" title="Wochenansicht">
                <v-icon class="mr-1">mdi-view-week</v-icon> Woche
              </v-btn>

              <v-btn icon @click="toggleSidebar" class="wc-icon-btn" :class="{ active: sidebarOpen || mobileTemplateSheet }" title="Vorlagen-Panel">
                <v-icon>mdi-dock-right</v-icon>
              </v-btn>

              <v-btn icon @click="isDarkMode = !isDarkMode" class="wc-icon-btn ml-1" title="Theme">
                <v-icon>{{ isDarkMode ? "mdi-weather-night" : "mdi-white-balance-sunny" }}</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <div v-show="!mobileDialogActive" class="flex-grow-1 d-flex overflow-hidden relative">
          <div class="flex-grow-1 d-flex flex-column relative" style="min-width: 0; border-right: 1px solid var(--border-light)">
            <div class="d-flex flex-grow-1 overflow-hidden">
              <!-- Time Axis -->
              <div
                class="time-axis custom-scrollbar"
                style="
                  width: 56px;
                  flex-shrink: 0;
                  overflow-y: hidden;
                  background: var(--bg-time-axis);
                  border-right: 1px solid var(--border-light);
                  position: relative;
                "
                ref="timeAxis">
                <div :style="{ height: headerOffset + 'px' }" style="flex-shrink: 0"></div>
                <div v-for="hour in hours" :key="hour" class="time-slot-label relative" :style="{ height: hourHeight + 'px' }">
                  <span class="wc-hour-label">{{ hour }}:00</span>
                  <div class="wc-hour-tick"></div>
                </div>
                <div :style="{ height: headerOffset + 'px' }" style="flex-shrink: 0"></div>
                <div
                  v-if="!loading && isToday && currentTimeTop > 0"
                  class="time-axis-current-badge"
                  :style="{ top: currentTimeTop + headerOffset + 'px' }"
                  style="z-index: 5">
                  {{ $moment().format("HH:mm") }}
                </div>
              </div>
              <!-- Column Scroller -->
              <div class="wc-scroller custom-scrollbar" ref="scroller" @scroll="syncScroll">
                <!-- Loading Skeleton -->
                <template v-if="loading">
                  <div v-for="n in 5" :key="'skel' + n" class="wc-skeleton-col">
                    <div class="wc-skeleton-header">
                      <div class="wc-skeleton-avatar wc-pulse"></div>
                      <div style="flex: 1">
                        <div class="wc-skeleton-line wc-pulse" style="width: 70%; height: 12px"></div>
                        <div class="wc-skeleton-line wc-pulse" style="width: 50%; height: 8px; margin-top: 6px"></div>
                      </div>
                    </div>
                    <div class="wc-skeleton-body">
                      <div
                        v-for="b in 3"
                        :key="'sb' + b"
                        class="wc-skeleton-block wc-pulse"
                        :style="{ top: 60 + b * 120 + 'px', height: 50 + Math.random() * 60 + 'px' }"></div>
                    </div>
                  </div>
                </template>

                <div
                  v-if="!loading && isToday && currentTimeTop > 0"
                  class="current-time-line"
                  :style="{ top: currentTimeTop + headerOffset + 'px', left: 0, right: 0, zIndex: 3 }"></div>

                <!-- Empty State -->
                <div
                  v-if="!loading && viewColumns.length === 0"
                  class="fill-height w-100 d-flex flex-column align-center justify-center"
                  style="min-height: 400px">
                  <div class="wc-empty-icon">
                    <v-icon size="40" color="var(--icon-color)">mdi-calendar-blank-outline</v-icon>
                  </div>
                  <div style="color: var(--text-main); font-size: 0.95rem; font-weight: 600; margin-top: 16px">Keine Schichten geplant</div>
                  <div style="color: var(--text-muted); font-size: 0.8rem; margin-top: 4px">Erstelle einen Einsatzplan für diesen Tag</div>
                </div>

                <!-- Columns -->
                <template v-if="!loading">
                  <work-calendar-column
                    v-for="col in mobileViewColumns"
                    :key="col.id"
                    :col="col"
                    :hours-count="hours.length"
                    :hour-height="hourHeight"
                    :header-offset="headerOffset"
                    :hover-state="hoverState"
                    :isDarkMode="isDarkMode"
                    @drag-over="handleDragOver"
                    @drop="handleDrop"
                    @click-item="handleBlockClick"
                    @click-empty="handleEmptyClick"
                    @click-header="openEmployeeSwitcher"
                    @open-note="openNoteDialog" />
                </template>
              </div>
            </div>
          </div>
          <!-- Desktop: Sidebar angedockt -->
          <transition name="slide-x-reverse-transition">
            <work-calendar-sidebar
              v-if="sidebarOpen && !$vuetify.breakpoint.mdAndDown"
              :templates="templates"
              :daily-tasks="dailyTasks"
              @create="openCreateTemplate"
              @drag-start="handleDragStart"
              @template-click="handleTemplateClick"
              @task-assign="openTaskAssignment" />
          </transition>

          <!-- Mobile/Tablet: Bottom-Sheet Dialog (Icon-on-Top Grid) -->
          <v-dialog
            v-model="mobileTemplateSheet"
            :fullscreen="$vuetify.breakpoint.xsOnly"
            :max-width="$vuetify.breakpoint.xsOnly ? undefined : 520"
            transition="dialog-bottom-transition"
            :content-class="$vuetify.breakpoint.xsOnly ? 'wc-mobile-sheet-fullscreen' : 'wc-mobile-sheet-dialog'">
            <v-card dark class="wc-mobile-sheet-card">
              <!-- Sheet Header -->
              <div class="wc-sheet-header pb-0">
                <div class="d-flex align-center mb-4" style="gap: 12px">
                  <div
                    class="wc-sheet-icon-box"
                    style="background: linear-gradient(135deg, rgba(76, 175, 80, 0.2), rgba(0, 0, 0, 0)); border: 1px solid rgba(76, 175, 80, 0.3)">
                    <v-icon small color="green accent-3">mdi-view-dashboard-outline</v-icon>
                  </div>
                  <div class="flex-grow-1">
                    <div class="wc-sheet-title" style="font-size: 1.1rem; font-weight: 700; letter-spacing: 0.5px">Vorlagen</div>
                  </div>
                  <v-btn
                    icon
                    small
                    @click="mobileTemplateSheet = false"
                    class="wc-sheet-close"
                    style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1)">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
                <!-- Modern Tabs Segmented Control -->
                <div class="wc-sb-tabs-container mb-2">
                  <div class="wc-sb-tab" :class="{ active: mobileActiveTab === 0 }" @click="mobileActiveTab = 0">
                    <v-icon small class="mr-1">mdi-checkbox-marked-circle-outline</v-icon> Ämtli
                  </div>
                  <div class="wc-sb-tab" :class="{ active: mobileActiveTab === 1 }" @click="mobileActiveTab = 1">
                    <v-icon small class="mr-1">mdi-calendar-multiselect</v-icon> Termine
                  </div>
                </div>
              </div>

              <!-- Sheet Body -->
              <div class="wc-sheet-body custom-scrollbar pt-2">
                <!-- TAB 0: ÄMTLI -->
                <div v-show="mobileActiveTab === 0">
                  <!-- Tägliche Ämtli (Daily Tasks) -->
                  <div class="wc-sheet-section" v-if="dailyTasks.length > 0">
                    <div class="wc-sheet-section-label" style="color: #66bb6a">Offene Ämtli (Heute)</div>
                    <div class="wc-sheet-grid-icons">
                      <div
                        v-for="tpl in dailyTasks"
                        :key="tpl.id"
                        class="wc-sheet-tile"
                        :class="{ 'wc-task-highlight': !tpl.isAssigned }"
                        :style="{ opacity: tpl.isAssigned ? 0.5 : 1, '--tile-color': tpl.isAssigned ? '#9e9e9e' : tpl.color || '#4CAF50' }"
                        @click="openTaskAssignment(tpl)">
                        <div class="wc-sheet-tile-icon" :style="{ background: tpl.isAssigned ? '#757575' : tpl.color || '#4CAF50' }">
                          <v-icon color="white" size="20">{{ tpl.isAssigned ? "mdi-check-circle" : tpl.icon || "mdi-checkbox-marked" }}</v-icon>
                        </div>
                        <div class="wc-sheet-tile-title" :style="{ textDecoration: tpl.isAssigned ? 'line-through' : 'none' }">{{ tpl.title }}</div>
                        <div class="wc-sheet-tile-meta font-weight-bold" :style="{ color: tpl.isAssigned ? 'rgba(255,255,255,0.4)' : '#66bb6a' }">
                          <v-icon x-small :color="tpl.isAssigned ? 'rgba(255,255,255,0.4)' : '#66bb6a'" class="mr-1">mdi-clock-outline</v-icon
                          >{{ tpl.time }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="dailyTasks.length === 0" class="text-center mt-10 opacity-40 mb-6">
                    <v-icon large class="mb-3" color="rgba(255,255,255,0.3)">mdi-check-all</v-icon>
                    <div style="font-size: 0.85rem">Alles erledigt für heute!</div>
                  </div>

                  <!-- Alle Ämtli Vorlagen -->
                  <div class="wc-sheet-section mt-6" v-if="mobileCustomTaskTemplates.length > 0">
                    <div class="wc-sheet-section-label" style="color: #42a5f5">Alle Ämtli Vorlagen</div>
                    <div class="wc-sheet-grid-icons">
                      <div
                        v-for="tpl in mobileCustomTaskTemplates"
                        :key="tpl._id || tpl.id"
                        class="wc-sheet-tile"
                        :style="{ '--tile-color': tpl.color || '#2196F3' }"
                        @click="openTaskAssignment(tpl)">
                        <div class="wc-sheet-tile-icon" :style="{ background: tpl.color || '#2196F3' }">
                          <v-icon color="white" size="20">{{ tpl.icon || "mdi-checkbox-marked" }}</v-icon>
                        </div>
                        <div class="wc-sheet-tile-title">{{ tpl.title }}</div>
                        <div class="wc-sheet-tile-meta">Ämtli Vorlage</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- TAB 1: TERMINE -->
                <div v-show="mobileActiveTab === 1">
                  <!-- Trainings & Checkups -->
                  <div class="wc-sheet-section">
                    <div class="wc-sheet-section-label">Trainings & Checkups</div>
                    <div class="wc-sheet-grid-icons">
                      <div
                        v-for="tpl in mobileTrainingTemplates"
                        :key="tpl.id"
                        class="wc-sheet-tile"
                        :style="{ '--tile-color': tpl.color }"
                        @click="handleMobileTemplateClick(tpl)">
                        <div class="wc-sheet-tile-icon" :style="{ background: tpl.color }">
                          <v-icon color="white" size="20">{{ tpl.icon }}</v-icon>
                        </div>
                        <div class="wc-sheet-tile-title">{{ tpl.title }}</div>
                        <div class="wc-sheet-tile-meta">{{ tpl.duration }} Min.</div>
                      </div>
                    </div>
                  </div>

                  <!-- Verträge & Management -->
                  <div class="wc-sheet-section">
                    <div class="wc-sheet-section-label">Verträge & Management</div>
                    <div class="wc-sheet-grid-icons">
                      <div
                        v-for="tpl in mobileContractTemplates"
                        :key="tpl.id"
                        class="wc-sheet-tile"
                        :style="{ '--tile-color': tpl.color }"
                        @click="handleMobileTemplateClick(tpl)">
                        <div class="wc-sheet-tile-icon" :style="{ background: tpl.color }">
                          <v-icon color="white" size="20">{{ tpl.icon }}</v-icon>
                        </div>
                        <div class="wc-sheet-tile-title">{{ tpl.title }}</div>
                        <div class="wc-sheet-tile-meta">Aktion</div>
                      </div>
                    </div>
                  </div>

                  <!-- Termine -->
                  <div class="wc-sheet-section">
                    <div class="wc-sheet-section-label">Termine</div>
                    <div class="wc-sheet-grid-icons">
                      <div
                        v-for="tpl in mobileStandardTemplates"
                        :key="tpl.id"
                        class="wc-sheet-tile"
                        :style="{ '--tile-color': tpl.color }"
                        @click="handleMobileTemplateClick(tpl)">
                        <div class="wc-sheet-tile-icon" :style="{ background: tpl.color }">
                          <v-icon color="white" size="20">{{ tpl.icon }}</v-icon>
                        </div>
                        <div class="wc-sheet-tile-title">{{ tpl.title }}</div>
                        <div class="wc-sheet-tile-meta">{{ tpl.duration }} Min.</div>
                      </div>
                    </div>
                  </div>

                  <!-- Eigene Vorlagen -->
                  <div class="wc-sheet-section" v-if="mobileCustomAppointmentTemplates.length > 0">
                    <div class="wc-sheet-section-label" style="color: #42a5f5">Eigene Vorlagen</div>
                    <div class="wc-sheet-grid-icons">
                      <div
                        v-for="tpl in mobileCustomAppointmentTemplates"
                        :key="tpl._id || tpl.id"
                        class="wc-sheet-tile"
                        :style="{ '--tile-color': tpl.color || '#2196F3' }"
                        @click="handleMobileTemplateClick(tpl)">
                        <div class="wc-sheet-tile-icon" :style="{ background: tpl.color || '#2196F3' }">
                          <v-icon color="white" size="20">{{ tpl.icon || "mdi-calendar" }}</v-icon>
                        </div>
                        <div class="wc-sheet-tile-title">{{ tpl.title }}</div>
                        <div class="wc-sheet-tile-meta">{{ tpl.duration }} Min.</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Sheet Footer -->
              <div class="wc-sheet-footer">
                <v-btn
                  block
                  depressed
                  class="wc-sheet-create-btn"
                  @click="
                    mobileTemplateSheet = false
                    openCreateTemplate()
                  ">
                  <v-icon left small>mdi-plus-circle</v-icon>
                  Neue Vorlage erstellen
                </v-btn>
              </div>
            </v-card>
          </v-dialog>

          <!-- Ämtli Smart-Assignment Dialog -->
          <v-dialog
            v-model="taskAssignDialog"
            :fullscreen="$vuetify.breakpoint.xsOnly"
            :max-width="$vuetify.breakpoint.xsOnly ? undefined : 520"
            transition="dialog-bottom-transition"
            :content-class="$vuetify.breakpoint.xsOnly ? 'wc-mobile-sheet-fullscreen' : 'wc-mobile-sheet-dialog'">
            <v-card dark class="wc-mobile-sheet-card">
              <!-- Header -->
              <div class="wc-sheet-header">
                <div class="d-flex align-center" style="gap: 10px">
                  <div
                    class="wc-sheet-tile-icon"
                    :style="{ background: taskAssignData.color || '#9C27B0', width: '36px', height: '36px', borderRadius: '10px' }">
                    <v-icon small color="white">{{ taskAssignData.icon || "mdi-checkbox-marked" }}</v-icon>
                  </div>
                  <div>
                    <div class="wc-sheet-title">{{ taskAssignData.title || "Ämtli zuweisen" }}</div>
                    <div class="wc-sheet-subtitle">Wähle Zeit & Mitarbeiter</div>
                  </div>
                </div>
                <v-btn icon small @click="taskAssignDialog = false" class="wc-sheet-close">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>

              <!-- Time Picker -->
              <div class="wc-task-time-picker">
                <div class="wc-task-time-label">
                  <v-icon x-small color="rgba(255,255,255,0.4)" class="mr-1">mdi-clock-outline</v-icon>
                  Uhrzeit
                </div>
                <div class="d-flex align-center" style="gap: 8px">
                  <v-btn icon small class="wc-task-time-btn" @click="adjustTaskTime(-15)">
                    <v-icon small>mdi-minus</v-icon>
                  </v-btn>
                  <input type="time" v-model="taskAssignTime" class="wc-task-time-input" @change="computeTaskAvailability" />
                  <v-btn icon small class="wc-task-time-btn" @click="adjustTaskTime(15)">
                    <v-icon small>mdi-plus</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Employee Availability Grid -->
              <div class="wc-sheet-body custom-scrollbar" style="padding-top: 8px">
                <div class="wc-sheet-section-label mb-3">Verfügbarkeit</div>

                <div v-if="taskAvailability.length === 0" class="text-center py-6" style="color: rgba(255, 255, 255, 0.3)">
                  <v-icon size="36" color="rgba(255,255,255,0.15)" class="mb-2">mdi-account-off</v-icon>
                  <div style="font-size: 0.8rem">Keine Mitarbeiter eingeplant</div>
                </div>

                <div
                  v-for="emp in taskAvailability"
                  :key="emp.id"
                  class="wc-task-emp-card"
                  :class="{
                    'wc-task-emp-free': emp.status === 'free',
                    'wc-task-emp-busy': emp.status === 'busy',
                    'wc-task-emp-noshift': emp.status === 'noshift',
                  }"
                  @click="emp.status !== 'noshift' && assignTask(emp)">
                  <v-avatar size="36" class="wc-task-emp-avatar">
                    <img :src="emp.picture ? '/img/profilbilder/' + emp.picture : '/img/default-avatar.png'" style="object-fit: cover" />
                  </v-avatar>
                  <div class="wc-task-emp-info">
                    <div class="wc-task-emp-name">{{ emp.firstName }}</div>
                    <div class="wc-task-emp-shift">
                      <v-icon x-small style="font-size: 10px" class="mr-1">mdi-clock-outline</v-icon>
                      {{ emp.shiftRange }}
                    </div>
                  </div>
                  <div class="wc-task-emp-status">
                    <template v-if="emp.status === 'free'">
                      <div class="wc-task-badge wc-task-badge-free">
                        <v-icon x-small color="white" style="font-size: 10px">mdi-check</v-icon>
                        Frei
                      </div>
                    </template>
                    <template v-else-if="emp.status === 'busy'">
                      <div class="wc-task-badge wc-task-badge-busy">
                        <v-icon x-small style="font-size: 10px">mdi-clock-alert</v-icon>
                        Belegt
                      </div>
                      <div v-if="emp.nextFree" class="wc-task-alt">Frei ab {{ emp.nextFree }}</div>
                    </template>
                    <template v-else>
                      <div class="wc-task-badge wc-task-badge-off">Keine Schicht</div>
                    </template>
                  </div>
                </div>
              </div>
            </v-card>
          </v-dialog>
        </div>

        <work-calendar-drop-dialog v-model="dropDialog" :data="pendingDrop" :employees="employeeList" @save="confirmDrop" />

        <work-calendar-edit-dialog
          v-model="editDialog"
          :edit-data="editData"
          :customer-details="customerDetails"
          :loading="customerLoading"
          :employees="employeeList"
          :current-date="currentDate"
          :isDarkMode="isDarkMode"
          @save="saveEdit"
          @delete="deleteItem"
          @action="triggerSpecialAction" />

        <v-dialog v-model="createTemplateDialog" max-width="580" persistent>
          <v-card dark class="rounded-xl" style="background: rgba(22, 22, 22, 0.98) !important; border: 1px solid rgba(255, 255, 255, 0.06)">
            <v-card-title
              class="white--text pa-6 pb-4"
              style="
                background: linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(0, 0, 0, 0));
                border-bottom: 1px solid rgba(255, 255, 255, 0.05);
              ">
              <div class="d-flex align-center w-100">
                <div
                  class="d-flex align-center justify-center mr-4"
                  style="
                    width: 44px;
                    height: 44px;
                    background: rgba(76, 175, 80, 0.15);
                    border-radius: 12px;
                    border: 1px solid rgba(76, 175, 80, 0.2);
                  ">
                  <v-icon color="green accent-3">mdi-plus</v-icon>
                </div>
                <div>
                  <div style="font-size: 1.25rem; font-weight: 700; letter-spacing: 0.5px">Neue Vorlage</div>
                  <div style="font-size: 0.75rem; color: rgba(255, 255, 255, 0.5); font-weight: 400; letter-spacing: 0.3px; margin-top: 2px">
                    Erstelle Termine oder wiederkehrende Ämtli
                  </div>
                </div>
              </div>
            </v-card-title>
            <v-card-text class="px-5 pt-2">
              <v-row dense>
                <v-col cols="12" class="mb-2">
                  <v-text-field v-model="newTemplate.title" label="Titel" outlined dark autofocus hide-details></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-model="newTemplate.type"
                    :items="[
                      { text: 'Termin', value: 'appointment' },
                      { text: 'Ämtli / Aufgabe', value: 'task' },
                    ]"
                    label="Typ"
                    outlined
                    dark
                    hide-details></v-select>
                </v-col>
                <v-col cols="6" v-if="newTemplate.type === 'appointment'">
                  <v-text-field v-model="newTemplate.duration" label="Dauer (Min)" type="number" outlined dark hide-details></v-text-field>
                </v-col>
                <v-col cols="12" class="mt-4">
                  <div
                    class="text-caption mb-2"
                    style="color: rgba(255, 255, 255, 0.3); font-size: 0.6rem; text-transform: uppercase; letter-spacing: 0.5px">
                    Farbe
                  </div>
                  <div class="d-flex flex-wrap" style="gap: 8px">
                    <div
                      v-for="c in colorOptions"
                      :key="c"
                      class="wc-color-dot"
                      :class="{ active: newTemplate.color === c }"
                      :style="{ background: c, width: '28px', height: '28px' }"
                      @click="newTemplate.color = c"></div>
                  </div>
                </v-col>
                <v-col cols="12" class="mt-4">
                  <div
                    class="text-caption mb-2"
                    style="color: rgba(255, 255, 255, 0.3); font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.5px">
                    Icon
                  </div>
                  <div class="d-flex flex-wrap custom-scrollbar" style="max-height: 160px; overflow-y: auto; gap: 6px">
                    <div
                      v-for="ic in iconOptions"
                      :key="ic.icon"
                      class="wc-icon-pick"
                      :class="{ active: newTemplate.icon === ic.icon }"
                      @click="newTemplate.icon = ic.icon">
                      <v-icon :color="newTemplate.icon === ic.icon ? 'white' : 'rgba(255,255,255,0.3)'">{{ ic.icon }}</v-icon>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" v-if="newTemplate.type === 'appointment'">
                  <v-switch
                    v-model="newTemplate.requiresCustomer"
                    label="Erfordert Kunde?"
                    dense
                    inset
                    dark
                    hide-details
                    class="mt-3"
                    color="blue accent-2"></v-switch>
                </v-col>
                <v-col cols="12" v-if="newTemplate.type === 'task'" class="mt-3">
                  <v-switch
                    v-model="newTemplate.hasFixedTimes"
                    label="An feste Uhrzeiten gebunden?"
                    dense
                    inset
                    dark
                    hide-details
                    class="mb-4"
                    color="green accent-3"></v-switch>
                  <div v-if="newTemplate.hasFixedTimes" class="mb-4">
                    <div
                      class="text-caption mb-2"
                      style="color: rgba(255, 255, 255, 0.3); font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.5px">
                      Uhrzeiten festlegen
                    </div>
                    <div class="d-flex flex-wrap gap-2 mb-3">
                      <div v-for="(time, idx) in newTemplate.defaultTimes" :key="'t' + idx" class="d-flex align-center">
                        <v-text-field
                          v-model="newTemplate.defaultTimes[idx]"
                          type="time"
                          dark
                          hide-details
                          outlined
                          class="wc-time-input"
                          style="max-width: 100px; font-size: 0.9rem"></v-text-field>
                        <v-btn icon @click="newTemplate.defaultTimes.splice(idx, 1)" color="red lighten-1" class="ml-2"
                          ><v-icon small>mdi-close</v-icon></v-btn
                        >
                      </div>
                      <v-btn
                        outlined
                        color="green accent-3"
                        style="
                          height: 56px;
                          border-radius: 8px;
                          border-style: dashed;
                          border-width: 1px;
                          text-transform: none;
                          letter-spacing: 0;
                          font-size: 0.85rem;
                        "
                        @click="newTemplate.defaultTimes.push('12:00')">
                        <v-icon left small>mdi-clock-plus-outline</v-icon> Zeit hinzufügen
                      </v-btn>
                    </div>
                  </div>
                  <div
                    class="text-caption mb-2 mt-4"
                    style="color: rgba(255, 255, 255, 0.3); font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.5px">
                    Checkliste für die Aufgabe
                  </div>
                  <div v-for="(item, idx) in newTemplate.checklist" :key="idx" class="d-flex align-center mb-1">
                    <v-text-field
                      v-model="item.text"
                      placeholder="Aufgabe..."
                      dense
                      dark
                      hide-details
                      outlined
                      class="mr-2"
                      style="font-size: 0.8rem; height: 36px"></v-text-field>
                    <v-btn icon x-small @click="newTemplate.checklist.splice(idx, 1)" color="red lighten-1"><v-icon x-small>mdi-close</v-icon></v-btn>
                  </div>
                  <v-btn
                    outlined
                    color="green accent-3"
                    class="mt-2"
                    style="
                      height: 36px;
                      border-radius: 8px;
                      border-style: dashed;
                      border-width: 1px;
                      text-transform: none;
                      letter-spacing: 0;
                      font-size: 0.8rem;
                      width: 100%;
                    "
                    @click="newTemplate.checklist.push({ text: '', isDone: false })">
                    <v-icon left small>mdi-plus</v-icon> Punkt hinzufügen
                  </v-btn>
                  <v-textarea
                    v-model="newTemplate.instructions"
                    label="Anleitung / Briefing"
                    outlined
                    dense
                    dark
                    hide-details
                    rows="3"
                    class="mt-4"
                    style="font-size: 0.8rem"></v-textarea>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions class="px-5 pb-4">
              <v-spacer></v-spacer>
              <v-btn text small @click="createTemplateDialog = false" style="color: rgba(255, 255, 255, 0.4); text-transform: none">Abbrechen</v-btn>
              <v-btn
                small
                depressed
                @click="saveTemplate"
                :disabled="!newTemplate.title"
                style="
                  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
                  color: white !important;
                  border-radius: 8px;
                  text-transform: none;
                  font-weight: 600;
                ">
                <v-icon left x-small>mdi-check</v-icon> Erstellen
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="noteDialog" max-width="440">
          <v-card dark class="rounded-xl" style="background: rgba(22, 22, 22, 0.98) !important; border: 1px solid rgba(255, 255, 255, 0.06)">
            <v-card-title style="font-size: 1rem">
              <v-icon left small color="blue lighten-2">mdi-message-text</v-icon>
              Notizen · {{ selectedCol?.info.firstName }}
            </v-card-title>
            <v-card-text class="pt-2">
              <div v-if="selectedCol?.notes.length > 0" class="mb-3">
                <div
                  v-for="note in selectedCol.notes"
                  :key="note._id"
                  class="d-flex align-start pa-3 mb-2 rounded-lg"
                  style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.04)">
                  <v-icon x-small color="blue lighten-2" class="mr-2 mt-1">mdi-message</v-icon>
                  <div class="flex-grow-1">
                    <div class="white--text text-body-2" style="white-space: pre-wrap; font-size: 0.85rem">{{ note.content }}</div>
                    <div class="text-caption mt-1" style="color: rgba(255, 255, 255, 0.3); font-size: 0.65rem">
                      {{ note.author ? note.author.vorname : "Team" }} · {{ $moment(note.createdAt).format("HH:mm") }}
                    </div>
                  </div>
                  <v-btn icon x-small @click="deleteNote(note._id)" style="color: rgba(255, 255, 255, 0.2)"><v-icon x-small>mdi-close</v-icon></v-btn>
                </div>
              </div>
              <div v-else class="text-center py-4" style="color: rgba(255, 255, 255, 0.2); font-size: 0.8rem">Keine Notizen</div>
              <v-textarea
                v-model="newNoteContent"
                outlined
                dense
                label="Nachricht..."
                rows="2"
                hide-details
                dark
                class="mt-2"
                style="font-size: 0.85rem"></v-textarea>
            </v-card-text>
            <v-card-actions class="px-5 pb-4">
              <v-spacer></v-spacer>
              <v-btn text small @click="noteDialog = false" style="color: rgba(255, 255, 255, 0.4); text-transform: none">Schließen</v-btn>
              <v-btn
                small
                depressed
                @click="createNote"
                :disabled="!newNoteContent"
                style="
                  background: linear-gradient(135deg, #42a5f5, #1565c0) !important;
                  color: white !important;
                  border-radius: 8px;
                  text-transform: none;
                  font-weight: 600;
                ">
                <v-icon left x-small>mdi-send</v-icon> Senden
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>

      <!-- Employee Switcher Dialog (Mobile) -->
      <v-dialog v-model="employeeSwitcherDialog" max-width="340" :fullscreen="$vuetify.breakpoint.xsOnly">
        <v-card dark class="rounded-xl" style="background: rgba(18, 18, 18, 0.98) !important; border: 1px solid rgba(255, 255, 255, 0.07)">
          <v-card-title class="d-flex align-center pa-4 pb-2">
            <span style="font-size: 0.95rem; font-weight: 700; color: white">Mitarbeiter wechseln</span>
            <v-spacer></v-spacer>
            <v-btn icon small @click="employeeSwitcherDialog = false" style="color: rgba(255, 255, 255, 0.4)">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-3">
            <div
              v-for="col in viewColumns"
              :key="col.id"
              class="d-flex align-center pa-3 mb-2 rounded-lg cursor-pointer"
              :style="
                col.id === (mobileViewColumns[0] && mobileViewColumns[0].id)
                  ? 'background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.1);'
                  : 'background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04);'
              "
              @click="switchEmployee(col)">
              <v-badge bottom overlap :color="col.liveStatus.color" dot offset-x="8" offset-y="8">
                <v-avatar size="48" class="mr-3">
                  <img :src="col.picture ? '/img/profilbilder/' + col.picture : '/img/default-avatar.png'" style="object-fit: cover" />
                </v-avatar>
              </v-badge>
              <div style="flex: 1; overflow: hidden">
                <div class="white--text font-weight-bold" style="font-size: 0.9rem">{{ col.firstName }}</div>
                <div class="d-flex align-center mt-1">
                  <v-icon x-small style="color: rgba(255, 255, 255, 0.3); font-size: 10px" class="mr-1">mdi-clock-outline</v-icon>
                  <span style="color: rgba(255, 255, 255, 0.4); font-size: 0.75rem">{{ col.shiftRange }}</span>
                </div>
              </div>
              <v-icon v-if="col.id === (mobileViewColumns[0] && mobileViewColumns[0].id)" small color="green accent-3">mdi-check-circle</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import moment from "moment"

// Import Sub-Components
import WorkCalendarColumn from "./WorkCalendarColumn"
import WorkCalendarSidebar from "./WorkCalendarSidebar"
import WorkCalendarDropDialog from "./WorkCalendarDropDialog"
import WorkCalendarEditDialog from "./WorkCalendarEditDialog"
import WorkCalendarWeek from "./WorkCalendarWeek"

import { mapActions } from "vuex"
export default {
  name: "WorkCalendar",
  components: {
    WorkCalendarColumn,
    WorkCalendarSidebar,
    WorkCalendarDropDialog,
    WorkCalendarEditDialog,
    WorkCalendarWeek,
  },
  data() {
    return {
      isDarkMode: true,
      viewMode: "day", // 'day' or 'week'

      currentDate: moment().format("YYYY-MM-DD"),
      loading: false,
      customerLoading: false, // NEW: Loading state for customer details
      columns: [],
      viewColumns: [],
      templates: [],
      liveAttendanceMap: {},
      studioId: userService.getUser().studio._id,

      startHour: 6,
      endHour: 23,
      headerOffset: 80,
      timerInterval: null,
      currentTimeTop: 0,
      sidebarOpen: true,
      hoverState: null,
      activeMobileColIndex: 0, // Mobile column tracking

      // Employee Switcher
      employeeSwitcherDialog: false,

      // Mobile Template Sheet
      mobileTemplateSheet: false,
      mobileActiveTab: 1,

      // Ämtli Smart-Assignment
      taskAssignDialog: false,
      taskAssignData: {},
      taskAssignTime: "",
      taskAvailability: [],

      // Dialogs
      dropDialog: false,
      pendingDrop: null,
      editDialog: false,
      editData: {},
      customerDetails: null,
      createTemplateDialog: false,
      newTemplate: {
        title: "",
        type: "appointment",
        duration: 60,
        color: "#2196F3",
        icon: "mdi-calendar",
        requiresCustomer: false,
        checklist: [],
        instructions: "",
        defaultTimes: [],
        hasFixedTimes: false,
      },
      noteDialog: false,
      selectedCol: null,
      newNoteContent: "",

      // Config Options
      iconOptions: [
        { name: "Training", icon: "mdi-dumbbell" },
        { name: "Plan", icon: "mdi-clipboard-list-outline" },
        { name: "Probetraining", icon: "mdi-account-star" },
        { name: "Check-Up", icon: "mdi-heart-pulse" },
        { name: "Ernährung", icon: "mdi-food-apple" },
        { name: "Kurs", icon: "mdi-yoga" },
        { name: "Verkauf", icon: "mdi-cash-register" },
        { name: "Meeting", icon: "mdi-account-group" },
        { name: "Büro", icon: "mdi-laptop" },
        { name: "Telefon", icon: "mdi-phone" },
        { name: "Reinigung", icon: "mdi-broom" },
        { name: "Wartung", icon: "mdi-tools" },
        { name: "Theke", icon: "mdi-coffee" },
        { name: "Rundgang", icon: "mdi-eye-check" },
        { name: "Müll", icon: "mdi-delete" },
        { name: "Inventar", icon: "mdi-package-variant" },
        { name: "Schliessen", icon: "mdi-door-closed-lock" },
        { name: "Öffnen", icon: "mdi-door-open" },
        { name: "Pause", icon: "mdi-pause-circle" },
        { name: "Event", icon: "mdi-party-popper" },
        { name: "Spray", icon: "mdi-spray-bottle" },
        { name: "Pflanzen", icon: "mdi-leaf" },
        { name: "Dokument", icon: "mdi-file-document-edit" },
        { name: "Check", icon: "mdi-check-all" },
        { name: "Uhrzeit", icon: "mdi-clock-outline" },
        { name: "Nachricht", icon: "mdi-message-text-outline" },
        { name: "Email", icon: "mdi-email-outline" },
        { name: "Wäsche", icon: "mdi-washing-machine" },
        { name: "Shirt", icon: "mdi-tshirt-crew" },
        { name: "Handtuch", icon: "mdi-hanger" },
        { name: "Korb", icon: "mdi-basket" },
        { name: "Kasse", icon: "mdi-account-cash" },
      ],
      colorOptions: ["#2196F3", "#4CAF50", "#FF9800", "#F44336", "#9C27B0", "#00BCD4", "#607D8B", "#E91E63"],
    }
  },
  computed: {
    hourHeight() {
      // Auf Handys kompakter (70px), auf Desktop normal (100px)
      return this.$vuetify.breakpoint.smAndDown ? 70 : 100
    },
    employeeList() {
      return this.viewColumns.map((col) => ({
        id: col.id,
        name: col.firstName, // oder col.info.firstName + ' ' + col.info.lastName
      }))
    },
    themeClasses() {
      return this.isDarkMode ? "theme--dark" : "theme--light"
    },
    hours() {
      const h = []
      for (let i = this.startHour; i <= this.endHour; i++) h.push(i)
      return h
    },
    isToday() {
      return moment(this.currentDate).isSame(moment(), "day")
    },
    mobileViewColumns() {
      if (this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.smAndDown) {
        if (this.viewColumns.length === 0) return []
        const maxIndex = Math.max(0, this.viewColumns.length - 1)
        const safeIndex = Math.min(this.activeMobileColIndex, maxIndex)
        return [this.viewColumns[safeIndex]]
      }
      return this.viewColumns
    },
    // Mobile template lists (same data as sidebar, but used in the bottom sheet)
    mobileTrainingTemplates() {
      return [
        {
          id: "w1",
          title: "Probetraining",
          icon: "mdi-account-star",
          color: "#F57C00",
          duration: 60,
          type: "appointment",
          specialType: "trial",
          requiresCustomer: true,
        },
        {
          id: "w2",
          title: "Trainingsplan",
          icon: "mdi-dumbbell",
          color: "#1976D2",
          duration: 60,
          type: "appointment",
          specialType: "workout",
          requiresCustomer: true,
        },
        {
          id: "w3",
          title: "Anamnese / Checkup",
          icon: "mdi-clipboard-pulse",
          color: "#00ACC1",
          duration: 30,
          type: "appointment",
          specialType: "anamnese",
          requiresCustomer: true,
        },
      ]
    },
    mobileContractTemplates() {
      return [
        {
          id: "a1",
          title: "Vertrag erstellen",
          icon: "mdi-file-document-edit",
          color: "#37474F",
          duration: 30,
          type: "appointment",
          specialType: "new_contract",
          requiresCustomer: false,
        },
        {
          id: "a2",
          title: "Upgrade / Anpassung",
          icon: "mdi-star-circle",
          color: "#455A64",
          duration: 30,
          type: "appointment",
          specialType: "upgrade",
          requiresCustomer: true,
        },
      ]
    },
    mobileStandardTemplates() {
      return [
        { id: "ap1", title: "Termin (Standard)", icon: "mdi-calendar", color: "#00963f", duration: 60, type: "appointment", requiresCustomer: true },
        { id: "ap2", title: "Kurzer Termin", icon: "mdi-clock-fast", color: "#00963f", duration: 15, type: "appointment", requiresCustomer: true },
      ]
    },
    mobileCustomAppointmentTemplates() {
      return this.templates.filter((t) => t.type === "appointment")
    },
    mobileCustomTaskTemplates() {
      return this.templates.filter((t) => t.type === "task")
    },
    // Ämtli die für heute noch offen sind (basierend auf defaultTimes)
    dailyTasks() {
      const tasks = []
      this.mobileCustomTaskTemplates.forEach((tpl) => {
        if (tpl.defaultTimes && tpl.defaultTimes.length > 0) {
          // Zähle wie viele Ämtli diesen Titels heute schon im Plan stehen (unabhängig von der Uhrzeit)
          let assignedCount = 0
          for (const col of this.viewColumns) {
            if (col.items) {
              assignedCount += col.items.filter((item) => item.isTask && item.title === tpl.title).length
            }
          }

          tpl.defaultTimes.forEach((time, index) => {
            // Wenn wir z.B. 2 defaultTimes haben, ist index 0 das erste und index 1 das zweite.
            // Solange assignedCount > index ist, ist dieses abgehakt!
            let isAssigned = assignedCount > index

            tasks.push({
              ...tpl,
              id: tpl._id + "-" + time, // Eindeutige ID für die Instanz
              time: time,
              isAssigned: isAssigned,
            })
          })
        }
      })
      // Sortiere nach Zeit
      tasks.sort((a, b) => a.time.localeCompare(b.time))
      return tasks
    },
    // Performance: Kalender-Grid verstecken wenn Fullscreen-Dialog auf Mobile offen ist
    mobileDialogActive() {
      if (!this.$vuetify || !this.$vuetify.breakpoint || !this.$vuetify.breakpoint.mdAndDown) return false
      return this.editDialog || this.dropDialog || this.mobileTemplateSheet || this.taskAssignDialog
    },
  },
  mounted() {
    this.fetchData()
    this.updateTimeLine()
    this.fetchLiveStatus()
    this.startClock()
  },
  beforeDestroy() {
    if (this.timerInterval) clearInterval(this.timerInterval)
  },
  methods: {
    ...mapActions("panelStore", ["selectCustomer", "clearCustomer", "setCustomerInfos"]),

    scrollToSmart() {
      // 1. Position berechnen
      let targetScrollTop = 0

      if (this.isToday) {
        const nowMinutes = moment().hours() * 60 + moment().minutes()
        const startMinutes = this.startHour * 60
        const offsetMinutes = Math.max(0, nowMinutes - startMinutes - 60)
        targetScrollTop = (offsetMinutes / 60) * this.hourHeight
      } else if (this.$vuetify.breakpoint.smAndDown && this.mobileViewColumns.length > 0) {
        const col = this.mobileViewColumns[0]
        let firstStart = "23:59"

        if (col.shifts) {
          col.shifts.forEach((s) =>
            s.times?.forEach((t) => {
              if (t.start < firstStart) firstStart = t.start
            }),
          )
        }
        if (col.appointments && firstStart === "23:59") {
          col.appointments.forEach((a) => {
            if (a.start < firstStart) firstStart = a.start
          })
        }

        if (firstStart !== "23:59") {
          const [h, m] = firstStart.split(":").map(Number)
          const targetMinutes = h * 60 + m
          const startMinutes = this.startHour * 60
          const offsetMinutes = Math.max(0, targetMinutes - startMinutes - 30)
          targetScrollTop = (offsetMinutes / 60) * this.hourHeight
        }
      }

      // 2. Nur noch 50ms warten (reicht aus, damit Vue die DOM-Höhe berechnet hat)
      setTimeout(() => {
        const scroller = this.$refs.scroller || document.querySelector(".wc-scroller")
        const timeAxis = this.$refs.timeAxis || document.querySelector(".time-axis")

        if (!scroller) return

        // Natives, flüssiges Browser-Scrollen verwenden!
        scroller.scrollTo({
          top: targetScrollTop,
          behavior: "smooth",
        })

        if (timeAxis) {
          timeAxis.scrollTo({
            top: targetScrollTop,
            behavior: "smooth",
          })
        }
      }, 50)
    },
    handleTemplateClick(template) {
      // Wenn wir auf Mobile/iPad sind, Sidebar schließen
      if (this.$vuetify.breakpoint.mdAndDown) {
        this.sidebarOpen = false
      }

      // Standard-Werte für den Dialog setzen
      const now = moment().minutes(Math.round(moment().minutes() / 15) * 15)
      const defaultTime = now.format("HH:mm")

      // Falls wir in der mobilen Ansicht nur eine Spalte sehen, nehmen wir diese
      const defaultCol = this.mobileViewColumns.length > 0 ? this.mobileViewColumns[0] : this.viewColumns[0]

      this.pendingDrop = {
        template: { ...template },
        col: defaultCol,
        time: defaultTime,
        description: "",
      }
      this.dropDialog = true
    },
    toggleSidebar() {
      if (this.$vuetify.breakpoint.mdAndDown) {
        // Mobile/Tablet: Bottom-Sheet öffnen
        this.mobileTemplateSheet = !this.mobileTemplateSheet
      } else {
        // Desktop: Sidebar togglen
        this.sidebarOpen = !this.sidebarOpen
      }
    },
    handleMobileTemplateClick(template) {
      this.mobileTemplateSheet = false
      this.handleTemplateClick(template)
    },
    // ===== ÄMTLI SMART ASSIGNMENT =====
    openTaskAssignment(template) {
      if (template.isAssigned) return // Bereits zugewiesen, Klick ignorieren

      this.mobileTemplateSheet = false
      this.taskAssignData = { ...template }

      if (template.time) {
        this.taskAssignTime = template.time
      } else {
        // Default: aktuelle Zeit, auf 15 Min gerundet
        const now = moment().minutes(Math.round(moment().minutes() / 15) * 15)
        this.taskAssignTime = now.format("HH:mm")
      }

      this.computeTaskAvailability()
      this.taskAssignDialog = true
    },
    computeTaskAvailability() {
      const time = this.taskAssignTime
      if (!time) {
        this.taskAvailability = []
        return
      }

      const checkMoment = moment(time, "HH:mm")
      const checkEnd = moment(time, "HH:mm").add(15, "minutes") // Ämtli Puffer

      this.taskAvailability = this.viewColumns.map((col) => {
        const result = {
          id: col.id,
          firstName: col.firstName,
          picture: col.picture,
          shiftRange: col.shiftRange,
          status: "noshift",
          nextFree: null,
        }

        // 1. Hat MA eine Schicht zu dieser Zeit?
        if (!col.backgrounds || col.backgrounds.length === 0) {
          result.status = "noshift"
          return result
        }

        let hasShift = false
        for (const bg of col.backgrounds) {
          const bgStart = moment(bg.start, "HH:mm")
          const bgEnd = moment(bg.end, "HH:mm")
          if (checkMoment.isSameOrAfter(bgStart) && checkEnd.isSameOrBefore(bgEnd)) {
            hasShift = true
            break
          }
        }

        if (!hasShift) {
          result.status = "noshift"
          return result
        }

        // 2. Ist der Slot frei? (Termine prüfen, Tasks blockieren nicht)
        let isBusy = false
        let busyEnd = null
        for (const item of col.items) {
          if (item.type === "system" || item.isTask) continue
          const itemStart = moment(item.start, "HH:mm")
          const itemEnd = item.end ? moment(item.end, "HH:mm") : moment(item.start, "HH:mm").add(30, "minutes")
          if (checkMoment.isBefore(itemEnd) && checkEnd.isAfter(itemStart)) {
            isBusy = true
            if (!busyEnd || itemEnd.isAfter(busyEnd)) {
              busyEnd = itemEnd
            }
          }
        }

        if (!isBusy) {
          result.status = "free"
        } else {
          result.status = "busy"
          // Nächsten freien Slot finden
          if (busyEnd) {
            // Prüfe ob nach dem belegten Termin noch innerhalb der Schicht Platz ist
            let nextSlot = moment(busyEnd)
            let foundFree = false
            const maxCheck = moment(busyEnd).add(4, "hours") // Max 4h voraus suchen

            while (nextSlot.isBefore(maxCheck)) {
              let slotFree = true
              const slotEnd = moment(nextSlot).add(15, "minutes")

              // Noch in Schicht?
              let inShift = false
              for (const bg of col.backgrounds) {
                if (nextSlot.isSameOrAfter(moment(bg.start, "HH:mm")) && slotEnd.isSameOrBefore(moment(bg.end, "HH:mm"))) {
                  inShift = true
                  break
                }
              }
              if (!inShift) break

              // Kollision?
              for (const item of col.items) {
                if (item.type === "system" || item.isTask) continue
                const iS = moment(item.start, "HH:mm")
                const iE = item.end ? moment(item.end, "HH:mm") : moment(item.start, "HH:mm").add(30, "minutes")
                if (nextSlot.isBefore(iE) && slotEnd.isAfter(iS)) {
                  slotFree = false
                  nextSlot = moment(iE) // Springe zum Ende dieses Termins
                  break
                }
              }

              if (slotFree) {
                result.nextFree = nextSlot.format("HH:mm")
                foundFree = true
                break
              }
            }
          }
        }
        return result
      })

      // Sortierung: Freie zuerst, dann belegte, dann ohne Schicht
      const order = { free: 0, busy: 1, noshift: 2 }
      this.taskAvailability.sort((a, b) => order[a.status] - order[b.status])
    },
    adjustTaskTime(delta) {
      const current = moment(this.taskAssignTime, "HH:mm")
      current.add(delta, "minutes")
      if (current.hours() < this.startHour) current.hours(this.startHour).minutes(0)
      if (current.hours() > this.endHour) current.hours(this.endHour).minutes(0)
      this.taskAssignTime = current.format("HH:mm")
      this.computeTaskAvailability()
    },
    async assignTask(emp) {
      const template = this.taskAssignData
      const time = emp.nextFree && emp.status === "busy" ? emp.nextFree : this.taskAssignTime

      try {
        await api.shiftManagement.createTask({
          date: this.currentDate,
          time,
          title: template.title,
          description: template.description || "",
          assignedEmployee: emp.id,
          icon: template.icon,
          color: template.color,
          checklist: template.checklist ? JSON.parse(JSON.stringify(template.checklist)) : [],
          instructions: template.instructions || "",
        })
        this.$toast.success(`${template.title} → ${emp.firstName} um ${time}`)
        this.taskAssignDialog = false
        this.fetchData(true)
      } catch (e) {
        this.$toast.error("Fehler beim Erstellen")
      }
    },
    openDayFromWeek(date) {
      this.currentDate = date
      this.viewMode = "day"
      this.fetchData()
    },

    async fetchData(silent = false) {
      if (!silent) this.loading = true
      try {
        const res = await api.shiftManagement.fetchDailyView(this.currentDate)
        this.columns = res.data.columns || []
        this.templates = res.data.templates || []
        await this.fetchLiveStatus()
        this.processViewData()
      } catch (e) {
        console.error(e)
      } finally {
        if (!silent) {
          this.loading = false
          this.$nextTick(() => {
            this.scrollToSmart()
          })
        }
      }
    },

    processViewData() {
      this.viewColumns = this.columns
        .filter((col) => col.shifts && col.shifts.length > 0) // FILTER: Nur mit Schicht
        .map((col) => ({
          id: col.info._id,
          firstName: col.info.firstName,
          picture: col.info.picture,
          info: col.info,
          shifts: col.shifts,
          tasks: col.tasks,
          appointments: col.appointments,

          shiftRange: this.getShiftRange(col),
          liveStatus: this.getLiveStatus(col),
          notes: col.notes,
          backgrounds: this.calculateBackgrounds(col),
          items: this.calculateItems(col),
        }))
    },

    // --- ITEM ACTIONS ---
    async handleBlockClick(block, col) {
      if (block.type === "system") return

      const raw = block.rawData || {}
      this.editData = {
        id: block._id,
        isTask: block.isTask,
        isPause: block.type === "pause",
        title: block.title,
        description: raw.description || "",
        color: block.borderColor,
        start: block.start,
        end: block.end,
        colId: col.id,
        specialType: raw.specialType || null,
        customer: raw.customer || null,
        guestData: raw.guestData || null,
        checklist: raw.checklist || [],
        instructions: raw.instructions || "",
        parentShiftId: block.parentShiftId,
        timeIndex: block.timeIndex,
        duration: block.end ? moment(block.end, "HH:mm").diff(moment(block.start, "HH:mm"), "minutes") : 30,
      }

      this.customerDetails = null

      // 🚀 FETCH EXTENDED CUSTOMER DATA — Pool-first approach
      if (this.editData.customer) {
        this.customerLoading = true
        this.editDialog = true // Open dialog immediately

        try {
          // 1. Schneller Pool-Lookup: Ist der Kunde eingecheckt?
          let poolData = null
          try {
            const lookupRes = await api.lookupCheckinPool(this.editData.customer._id)
            if (lookupRes && lookupRes.inPool) {
              poolData = lookupRes
            }
          } catch (lookupErr) {
            // 404 = nicht im Pool → kein Problem, wir laden dann komplett
          }

          // 2. Fallback: Komplettes Laden, wenn nicht im Pool
          if (!poolData) {
            poolData = await api.fetchCustomerInfosAndUpdatePool({ roodyUserId: this.editData.customer._id })
          }

          this.customerDetails = poolData
        } catch (e) {
          console.error("Customer info fetch failed", e)
        } finally {
          this.customerLoading = false
        }
      } else {
        this.editDialog = true
      }
    },

    // --- (Rest of the methods remain unchanged: isTimeSlotFree, handleDragOver, handleDrop, confirmDrop, saveEdit, deleteItem, triggerSpecialAction, etc.) ---
    isTimeSlotFree(col, startStr, endStr, excludeId = null, isTask = false) {
      const newStart = moment(startStr, "HH:mm")
      const newEnd = moment(endStr, "HH:mm")
      if (!col.backgrounds || !col.backgrounds.length) return "Mitarbeiter hat keine Schicht."
      let covered = false
      for (const bg of col.backgrounds) {
        const bgStart = moment(bg.start, "HH:mm")
        const bgEnd = moment(bg.end, "HH:mm")
        if (newStart.isSameOrAfter(bgStart) && newEnd.isSameOrBefore(bgEnd)) {
          covered = true
          break
        }
      }
      if (!covered) return "Außerhalb der Arbeitszeit."

      // Ämtli blockieren keine Zeit im klassischen Sinne und dürfen parallel laufen
      if (isTask) return true

      for (const item of col.items) {
        if (item._id === excludeId) continue
        if (item.type === "system" || item.isTask) continue // System-Items und Ämtli blockieren nicht
        const itemStart = moment(item.start, "HH:mm")
        let itemEndStr = item.end || moment(item.start, "HH:mm").add(30, "minutes").format("HH:mm")
        const itemEnd = moment(itemEndStr, "HH:mm")
        if (newStart.isBefore(itemEnd) && newEnd.isAfter(itemStart)) return `Kollision mit "${item.title}"`
      }
      return true
    },
    handleDragStart(tpl) {
      this._draggedDuration = tpl.duration || 60
    },
    handleDragOver({ event, colId, relativeY }) {
      const hoursClicked = Math.max(0, relativeY / this.hourHeight)
      const totalMin = hoursClicked * 60
      const hour = this.startHour + Math.floor(totalMin / 60)
      const min = Math.round((totalMin % 60) / 15) * 15
      const timeStr = moment().hour(hour).minute(min).format("HH:mm")
      const topPx = (Math.max(0, (hour - this.startHour) * 60 + min) / 60) * this.hourHeight
      const col = this.viewColumns.find((c) => c.id === colId)
      const dragDuration = this._draggedDuration || 60
      const endTime = moment(timeStr, "HH:mm").add(dragDuration, "minutes").format("HH:mm")
      const ghostHeight = (dragDuration / 60) * this.hourHeight
      const validation = this.isTimeSlotFree(col, timeStr, endTime)
      const error = validation === true ? null : validation

      if (this.hoverState && this.hoverState.colId === colId && this.hoverState.time === timeStr) {
        return
      }
      this.hoverState = { colId: colId, time: timeStr, top: topPx, height: ghostHeight, error }
    },
    onDragEndGlobal() {
      this.hoverState = null
    },
    openEmployeeSwitcher(col) {
      if (this.$vuetify.breakpoint.smAndDown) {
        this.employeeSwitcherDialog = true
      }
    },
    switchEmployee(col) {
      const idx = this.viewColumns.findIndex((c) => c.id === col.id)
      if (idx !== -1) this.activeMobileColIndex = idx
      this.employeeSwitcherDialog = false
      this.scrollToSmart()
    },
    prevMobileCol() {
      if (this.activeMobileColIndex > 0) this.activeMobileColIndex--
    },
    nextMobileCol() {
      if (this.activeMobileColIndex < this.viewColumns.length - 1) this.activeMobileColIndex++
    },
    handleDrop({ event, col }) {
      this.hoverState = null
      const tplData = event.dataTransfer.getData("templateObj")
      if (!tplData) return
      const template = JSON.parse(tplData)
      const rect = event.currentTarget.querySelector(".lane-body").getBoundingClientRect()
      const relativeY = event.clientY - rect.top
      const totalMin = Math.max(0, relativeY / this.hourHeight) * 60
      const hour = this.startHour + Math.floor(totalMin / 60)
      const min = Math.round((totalMin % 60) / 15) * 15
      const timeStr = moment().hour(hour).minute(min).format("HH:mm")
      const duration = template.duration || 60
      const endM = moment(timeStr, "HH:mm").add(duration, "minutes")
      const valid = this.isTimeSlotFree(col, timeStr, endM.format("HH:mm"))
      if (valid !== true) {
        if (valid === "Außerhalb der Arbeitszeit.") {
          if (!confirm("Termin liegt außerhalb der Arbeitszeit. Trotzdem einfügen?")) return
        } else {
          this.$toast.error(valid)
          return
        }
      }
      this.pendingDrop = { template: { ...template }, col, time: timeStr, description: "" }
      this.dropDialog = true
    },
    async confirmDrop(data) {
      // colId auslesen (kommt aus unserem neuen Select)
      const { template, time, duration, description, customer, colId } = data

      // Richtige Spalte anvisieren anhand des Dropdowns im Dialog
      const col = this.viewColumns.find((c) => c.id === (colId || data.col.id))

      const endM = moment(`${this.currentDate} ${time}`, "YYYY-MM-DD HH:mm").add(duration, "minutes")
      const valid = this.isTimeSlotFree(col, time, endM.format("HH:mm"))

      if (valid !== true) {
        if (valid === "Außerhalb der Arbeitszeit.") {
          if (!confirm("Termin liegt außerhalb der Arbeitszeit. Trotzdem einfügen?")) return
        } else {
          this.$toast.error(valid)
          return
        }
      }
      try {
        if (template.type === "task") {
          await api.shiftManagement.createTask({
            date: this.currentDate,
            time,
            title: template.title,
            description,
            assignedEmployee: col.info._id,
            icon: template.icon,
            color: template.color,
            checklist: template.checklist ? JSON.parse(JSON.stringify(template.checklist)) : [],
            instructions: template.instructions || "",
          })
        } else {
          const appointmentData = {
            date: this.currentDate,
            start: time,
            end: endM.format("HH:mm"),
            title: template.title,
            description,
            assignedEmployee: col.info._id,
            customer: customer ? customer : null,
            icon: template.icon,
            color: template.color,
            specialType: template.specialType || null,
          }
          if (data.guestData) {
            appointmentData.rawData = { guestData: data.guestData }
          }
          await api.shiftManagement.createAppointment(appointmentData)
        }
        this.$toast.success("Erstellt!")
        this.dropDialog = false
        this.fetchData(true)
      } catch (e) {
        this.$toast.error("Fehler beim Speichern")
      }
    },
    async saveEdit(data) {
      // Validierung (Zeitraum prüfen)
      const targetCol = this.viewColumns.find((c) => c.id === data.colId)

      let endStr = data.end
      if (!data.isTask) {
        endStr = moment(data.start, "HH:mm").add(data.duration, "minutes").format("HH:mm")
      } else {
        // Ämtli haben kein fixes Ende im Backend, wir geben ihm 15 Min Puffer für die Schicht-Überprüfung
        endStr = moment(data.start, "HH:mm").add(15, "minutes").format("HH:mm")
      }

      if (data.date === this.currentDate) {
        const valid = this.isTimeSlotFree(targetCol, data.start, endStr, data.id, data.isTask)
        if (valid !== true) {
          if (valid === "Außerhalb der Arbeitszeit.") {
            if (!confirm("Termin liegt außerhalb der Arbeitszeit. Trotzdem speichern?")) return
          } else {
            this.$toast.error(valid)
            return
          }
        }
      }

      try {
        if (data.isTask) {
          const payload = {
            title: data.title,
            description: data.description,
            color: data.color,
            time: data.start, // Tasks use time
            date: data.date,
            assignedEmployee: data.colId,
            checklist: data.checklist || [],
            instructions: data.instructions || "",
          }
          await api.shiftManagement.updateTask(data.id, payload)
        } else if (data.isPause && data.parentShiftId) {
          // PAUSE UPDATE (ShiftPlan)
          const col = this.columns.find((c) => String(c.info._id) === String(data.colId))
          if (!col) throw new Error("Mitarbeiter nicht gefunden")

          const shift = col.shifts.find((s) => String(s._id) === String(data.parentShiftId))
          if (!shift) throw new Error("Schicht nicht gefunden")

          const newTimes = JSON.parse(JSON.stringify(shift.times || []))
          if (newTimes[data.timeIndex]) {
            newTimes[data.timeIndex].start = data.start
            newTimes[data.timeIndex].end = endStr
          }

          await api.shiftManagement.updateShift(data.parentShiftId, { times: newTimes })
        } else {
          // TERMIN UPDATE
          const payload = {
            title: data.title,
            description: data.description,
            color: data.color,
            start: data.start,
            end: endStr,
            status: data.status,
            assignedEmployee: data.colId,
            date: data.date,
          }
          await api.shiftManagement.updateAppointment(data.id, payload)
        }

        this.$toast.success("Gespeichert")
        this.editDialog = false

        // Wenn das Datum geändert wurde, müssen wir evtl. zu dem Tag springen oder einfach neu laden
        if (data.date !== this.currentDate) {
          const switchDate = confirm("Termin wurde auf einen anderen Tag verschoben. Zu diesem Tag springen?")
          if (switchDate) {
            this.currentDate = data.date
          }
        }

        this.fetchData(true) // Refresh silent
      } catch (e) {
        console.log(e)
        this.$toast.error("Fehler beim Speichern")
      }
    },
    async deleteItem(data) {
      if (!confirm("Wirklich löschen?")) return
      try {
        if (data.isTask) {
          await api.shiftManagement.deleteTask(data.id)
        } else if (data.isPause && data.parentShiftId) {
          // PAUSE DELETE (ShiftPlan)
          const col = this.columns.find((c) => String(c.info._id) === String(data.colId))
          if (!col) throw new Error("Mitarbeiter nicht gefunden")

          const shift = col.shifts.find((s) => String(s._id) === String(data.parentShiftId))
          if (!shift) throw new Error("Schicht nicht gefunden")

          const newTimes = JSON.parse(JSON.stringify(shift.times || []))
          if (newTimes[data.timeIndex]) {
            newTimes.splice(data.timeIndex, 1)
          }

          await api.shiftManagement.updateShift(data.parentShiftId, { times: newTimes })
        } else {
          await api.shiftManagement.deleteAppointment(data.id)
        }
        this.editDialog = false
        this.fetchData(true)
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    triggerSpecialAction(data) {
      const type = data.specialType
      const cid = data.customer._id
      if (type === "workout") this.$router.push(`/workouts/createnew?userId=${cid}`)
      else if (type === "anamnese") this.$router.push(`/dashboard/user/search/${cid}`)
      else if (type === "upgrade") this.$router.push(`/dashboard/contract?userId=${cid}&upgrade=true`)
    },
    openCreateTemplate() {
      this.newTemplate = {
        title: "",
        type: "appointment",
        duration: 60,
        color: "#2196F3",
        icon: "mdi-calendar",
        requiresCustomer: false,
        checklist: [],
        instructions: "",
        defaultTimes: [],
        hasFixedTimes: false,
      }
      this.createTemplateDialog = true
    },
    async saveTemplate() {
      try {
        if (this.newTemplate.type === "task") {
          if (!this.newTemplate.hasFixedTimes) {
            this.newTemplate.defaultTimes = [] // Reset if switched off
          } else {
            this.newTemplate.defaultTimes = this.newTemplate.defaultTimes.filter((t) => t && t.trim() !== "")
          }
        }
        await api.shiftManagement.createTemplate(this.newTemplate)
        this.$toast.success("Vorlage gespeichert")
        this.createTemplateDialog = false
        this.fetchData(true)
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    openNoteDialog(col) {
      this.selectedCol = col
      this.newNoteContent = ""
      this.noteDialog = true
    },
    async createNote() {
      try {
        const date = this.selectedCol.date || this.currentDate
        const targetId = this.selectedCol.info.userId || this.selectedCol.info._id
        const res = await api.shiftManagement.createNote({ date: date, targetEmployee: targetId, content: this.newNoteContent })
        this.$toast.success("Gesendet")
        this.newNoteContent = ""
        // UI sofort aktualisieren
        if (!this.selectedCol.notes) this.$set(this.selectedCol, "notes", [])
        this.selectedCol.notes.push(res.data)
        this.fetchData() // Lade Daten im Hintergrund neu
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    async deleteNote(id) {
      if (!confirm("Löschen?")) return
      try {
        await api.shiftManagement.deleteNote(id)
        this.fetchData()
      } catch (e) {}
    },
    handleEmptyClick(e, col) {
      if (this.$vuetify.breakpoint.mdAndDown) {
        this.mobileTemplateSheet = true
      } else {
        if (!this.sidebarOpen) {
          this.sidebarOpen = true
        }
        this.$toast.info("Nutze die Vorlagen rechts!")
      }
    },
    getTopFromTime(timeStr) {
      if (!timeStr) return 0
      const [h, m] = timeStr.split(":").map(Number)
      return (((h - this.startHour) * 60 + m) / 60) * this.hourHeight
    },
    getTop(hour, minute = 0) {
      return (((hour - this.startHour) * 60 + minute) / 60) * this.hourHeight
    },
    hexToRgba(hex, alpha) {
      if (!hex) return `rgba(100,100,100, ${alpha})`
      if (hex.startsWith("#")) {
        const r = parseInt(hex.slice(1, 3), 16)
        const g = parseInt(hex.slice(3, 5), 16)
        const b = parseInt(hex.slice(5, 7), 16)
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
      }
      return hex
    },
    getShiftRange(col) {
      if (!col.shifts.length) return "-"
      let all = []
      col.shifts.forEach((s) => {
        if (s.times) all.push(...s.times)
      })
      if (!all.length) return "-"
      all.sort((a, b) => a.start.localeCompare(b.start))
      return `${all[0].start} - ${all[all.length - 1].end}`
    },
    calculateBackgrounds(col) {
      return col.shifts
        .map((s) => {
          if (!s.times || !s.times.length) return null
          const sorted = [...s.times].sort((a, b) => a.start.localeCompare(b.start))
          const top = this.getTopFromTime(sorted[0].start)
          const bottom = this.getTopFromTime(sorted[sorted.length - 1].end)
          const color = s.shiftTemplate?.color || "#333"
          return {
            top,
            height: bottom - top,
            color: color,
            bgColor: this.hexToRgba(color, 0.1),
            start: sorted[0].start,
            end: sorted[sorted.length - 1].end,
          }
        })
        .filter((x) => x)
    },
    calculateItems(col) {
      let items = []
      col.shifts.forEach((s) => {
        const times = s.times || []
        if (!times.length) return
        times.forEach((t, idx) => {
          if (t.type === "Pause")
            items.push({
              title: "Pause",
              start: t.start,
              end: t.end,
              top: this.getTopFromTime(t.start),
              height: this.getTopFromTime(t.end) - this.getTopFromTime(t.start),
              class: "item-pause", // Spezielle Klasse für Schraffur bleibt
              borderColor: "#FF9800",
              icon: "mdi-coffee",
              iconColor: "orange",
              type: "pause",
              parentShiftId: s._id,
              timeIndex: idx,
            })
        })
        if (s.shiftTemplate) {
          if (s.shiftTemplate.isOpeningShift) {
            const sorted = [...times].sort((a, b) => a.start.localeCompare(b.start))
            if (sorted.length > 0) {
              const startStr = sorted[0].start
              const endStr = moment(startStr, "HH:mm").add(30, "minutes").format("HH:mm")
              const top = this.getTopFromTime(startStr)
              items.push({
                title: "Centeröffnung",
                subtitle: "Fix",
                start: startStr,
                end: endStr,
                top,
                height: this.getTopFromTime(endStr) - top,
                class: "item-system glass-red",
                borderColor: "#D32F2F",
                icon: "mdi-door-open",
                iconColor: "red lighten-3",
                type: "system",
              })
            }
          }
          if (s.shiftTemplate.isClosingShift) {
            const sorted = [...times].sort((a, b) => a.start.localeCompare(b.start))
            if (sorted.length > 0) {
              const endStr = sorted[sorted.length - 1].end
              const startStr = moment(endStr, "HH:mm").subtract(30, "minutes").format("HH:mm")
              const top = this.getTopFromTime(startStr)
              items.push({
                title: "Centerschliessung",
                subtitle: "Fix",
                start: startStr,
                end: endStr,
                top,
                height: this.getTopFromTime(endStr) - top,
                class: "item-system glass-red",
                borderColor: "#D32F2F",
                icon: "mdi-door-closed",
                iconColor: "red lighten-3",
                type: "system",
              })
            }
          }
        }
      })
      col.tasks.forEach((t) => {
        const color = t.color || "#9C27B0" // Default Lila
        items.push({
          _id: t._id,
          title: t.title,
          subtitle: t.description || "Aufgabe",
          start: t.time,
          end: "",
          top: this.getTopFromTime(t.time),
          height: (30 / 60) * this.hourHeight,

          // HIER IST DIE ÄNDERUNG: Kein 'class': 'glass-purple' mehr!
          // Stattdessen direkter Style:
          style: {
            background: `linear-gradient(135deg, ${this.hexToRgba(color, 0.35)} 0%, ${this.hexToRgba(color, 0.15)} 100%)`,
            border: `1px solid ${this.hexToRgba(color, 0.5)}`,
            boxShadow: `0 4px 15px ${this.hexToRgba(color, 0.15)}`,
          },

          borderColor: color, // Für den linken Rand (optional)
          icon: t.icon || "mdi-checkbox-marked",
          iconColor: "white",
          isTask: true,
          rawData: t,
        })
      })

      // 3. Appointments (Termine) - Jetzt dynamisch!
      col.appointments.forEach((a) => {
        const top = this.getTopFromTime(a.start)
        const color = a.color || "#2196F3" // Default Blau

        items.push({
          _id: a._id,
          title: a.title,
          subtitle: a.customer ? `${a.customer.vorname} ${a.customer.nachname}` : a.description,
          start: a.start,
          end: a.end,
          top,
          height: this.getTopFromTime(a.end) - top,

          // HIER IST DIE ÄNDERUNG: Dynamischer Glass-Effekt basierend auf Termin-Farbe
          style: {
            background: `linear-gradient(135deg, ${this.hexToRgba(color, 0.35)} 0%, ${this.hexToRgba(color, 0.15)} 100%)`,
            border: `1px solid ${this.hexToRgba(color, 0.5)}`,
            boxShadow: `0 4px 15px ${this.hexToRgba(color, 0.15)}`,
          },

          borderColor: color,
          icon: a.icon || "mdi-calendar",
          iconColor: "white",
          isAppt: true,
          rawData: a,
          status: a.status || "planned", // Status durchreichen!
        })
      })
      return items
    },
    getLiveStatus(col) {
      return this.liveAttendanceMap[col.info.employeeId]
        ? { active: true, color: "green accent-4", since: this.liveAttendanceMap[col.info.employeeId].checkin || "Jetzt" }
        : { active: false, color: "grey darken-3", since: "" }
    },
    changeDate(d) {
      this.currentDate = moment(this.currentDate).add(d, "days").format("YYYY-MM-DD")
      this.fetchData()
    },
    jumpToToday() {
      this.currentDate = moment().format("YYYY-MM-DD")
      this.fetchData()
    },
    syncScroll(e) {
      if (this.$refs.timeAxis) this.$refs.timeAxis.scrollTop = e.target.scrollTop
    },
    scrollToNow() {
      if (!this.$refs.scroller) return
      const nowMinutes = moment().hours() * 60 + moment().minutes()
      const offset = Math.max(0, nowMinutes - this.startHour * 60 - 60)
      this.$refs.scroller.scrollTop = (offset / 60) * this.hourHeight
    },
    startClock() {
      this.timerInterval = setInterval(() => {
        this.updateTimeLine()
        this.fetchLiveStatus()
      }, 30000)
    },
    updateTimeLine() {
      console.log(this.currentTimeTop)
      if (!this.isToday) {
        this.currentTimeTop = -100
        return
      }
      const now = moment()
      const h = now.hour()
      if (h < this.startHour || h > this.endHour) {
        this.currentTimeTop = -100
        return
      }
      this.currentTimeTop = this.getTop(h, now.minute())
    },
    async fetchLiveStatus() {
      try {
        let data = await api.shiftManagement.fetchAttendanceTrainers(this.studioId)
        const map = {}
        data.forEach((w) => {
          map[w._id] = w
        })
        this.liveAttendanceMap = map
      } catch (e) {}
    },
  },
}
</script>

<style scoped>
::v-deep .studio-cockpit-wrapper {
  --bg-main: #0a0a0a;
  --bg-header: rgba(12, 12, 12, 0.98);
  --bg-time-axis: rgba(8, 8, 8, 0.95);
  --bg-panel: rgba(255, 255, 255, 0.05); /* cards hover, empty state */
  --bg-panel-hover: rgba(255, 255, 255, 0.08);
  --bg-card: #232323;
  --bg-sidebar: rgba(12, 12, 12, 0.98);
  --bg-block: rgba(35, 35, 35, 0.95);
  --bg-skeleton: rgba(255, 255, 255, 0.04);
  --bg-time-line: rgba(255, 255, 255, 0.06);
  --text-main: #ffffff;
  --text-muted: rgba(255, 255, 255, 0.45);
  --text-disabled: rgba(255, 255, 255, 0.25);
  --border-light: rgba(255, 255, 255, 0.04);
  --border-medium: rgba(255, 255, 255, 0.06);
  --border-strong: rgba(255, 255, 255, 0.12);
  --icon-color: rgba(255, 255, 255, 0.5);
  --icon-muted: rgba(255, 255, 255, 0.35);
  --shadow-color: rgba(0, 0, 0, 0.5);

  background: var(--bg-main) !important;
  color: var(--text-main);
  transition: background 0.3s ease, color 0.3s ease;
}
.studio-cockpit-wrapper {
  flex: 1;
  min-height: 0; /* Wichtig, damit Flexbox das Scrollen erlaubt */
  overflow: hidden;
  background: var(--bg-main); /* Sicherheitshalber: Füllt den letzten Rest mit deiner Dark-Mode Farbe */
}
::v-deep .studio-cockpit-wrapper.theme--light {
  --bg-main: #fafafa;
  --bg-header: #ffffff;
  --bg-time-axis: #fcfcfc;
  --bg-panel: rgba(0, 0, 0, 0.02);
  --bg-panel-hover: rgba(0, 0, 0, 0.04);
  --bg-card: #ffffff;
  --bg-sidebar: #ffffff;
  --bg-block: #ffffff;
  --bg-skeleton: rgba(0, 0, 0, 0.04);
  --bg-time-line: rgba(0, 0, 0, 0.04);
  --text-main: #333333;
  --text-muted: rgba(0, 0, 0, 0.5);
  --text-disabled: rgba(0, 0, 0, 0.3);
  --border-light: rgba(0, 0, 0, 0.03);
  --border-medium: rgba(0, 0, 0, 0.06);
  --border-strong: rgba(0, 0, 0, 0.12);
  --icon-color: rgba(0, 0, 0, 0.45);
  --icon-muted: rgba(0, 0, 0, 0.35);
  --shadow-color: rgba(0, 0, 0, 0.05);
}
.work-calendar {
  display: flex;
  flex-direction: column;
  flex: 1 1 100%;
  width: 100%;
  height: 100%;

  /* DIE NOTBREMSE: Zwingt den Kalender zu stoppen, bevor er zu groß wird! */
  max-height: 100vh;
  max-height: 100dvh; /* Modernes CSS für Mobile-Browser (ignoriert Adressleisten) */

  overflow: hidden;
}
.v-input input,
.v-select__selection {
  color: var(--text-main) !important;
}
.v-icon {
  color: var(--icon-color);
}
/* ===== MOBILE BOTTOM-SHEET DIALOG ===== */
::v-deep .wc-mobile-sheet-dialog {
  align-self: flex-end !important;
  margin-bottom: 0 !important;
  border-radius: 20px 20px 0 0 !important;
  max-height: 85vh !important;
}
::v-deep .wc-mobile-sheet-fullscreen .v-card {
  border-radius: 0 !important;
}

.wc-mobile-sheet-card {
  background: rgba(16, 16, 16, 0.98) !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 20px 20px 0 0 !important;
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  overflow: hidden;
}

.wc-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}
.wc-sheet-icon-box {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #4caf50, #2e7d32);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.wc-sheet-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}
.wc-sheet-subtitle {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 2px;
}
.wc-sheet-close {
  color: rgba(255, 255, 255, 0.4) !important;
  background: rgba(255, 255, 255, 0.05) !important;
  border-radius: 10px !important;
}
.wc-sheet-close:hover {
  color: white !important;
  background: rgba(255, 255, 255, 0.1) !important;
}

.wc-sheet-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  -webkit-overflow-scrolling: touch;
}

.wc-sheet-section {
  margin-bottom: 20px;
}
.wc-sheet-section:last-child {
  margin-bottom: 8px;
}
.wc-sheet-section-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 10px;
  padding-left: 2px;
}

.wc-sheet-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.wc-sheet-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  overflow: hidden;
}
.wc-sheet-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--card-accent);
  border-radius: 0 3px 3px 0;
  opacity: 0;
  transition: opacity 0.2s ease;
}
.wc-sheet-card:active {
  transform: scale(0.98);
  background: rgba(255, 255, 255, 0.06);
}
.wc-sheet-card:active::before {
  opacity: 1;
}
@media (hover: hover) {
  .wc-sheet-card:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.08);
    transform: translateX(4px);
  }
  .wc-sheet-card:hover::before {
    opacity: 1;
  }
}

.wc-sheet-card-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.wc-sheet-card-info {
  flex: 1;
  min-width: 0;
}
.wc-sheet-card-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.wc-sheet-card-meta {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.3);
  margin-top: 2px;
}
.wc-sheet-card-arrow {
  color: rgba(255, 255, 255, 0.15) !important;
  flex-shrink: 0;
  transition: color 0.2s;
}
.wc-sheet-card:active .wc-sheet-card-arrow {
  color: rgba(255, 255, 255, 0.4) !important;
}

.wc-sheet-footer {
  padding: 12px 20px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}
/* Safe area for devices with home indicator */
@supports (padding-bottom: env(safe-area-inset-bottom)) {
  .wc-sheet-footer {
    padding-bottom: calc(20px + env(safe-area-inset-bottom));
  }
}

.wc-sheet-create-btn {
  background: rgba(76, 175, 80, 0.12) !important;
  color: #66bb6a !important;
  border: 1px solid rgba(76, 175, 80, 0.2) !important;
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.85rem !important;
  letter-spacing: 0 !important;
  height: 46px !important;
  transition: all 0.2s ease;
}
.wc-sheet-create-btn:active {
  background: rgba(76, 175, 80, 0.2) !important;
  transform: scale(0.98);
}

/* ===== ICON-ON-TOP GRID TILES ===== */
.wc-sheet-grid-icons {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.wc-time-input input[type="time"]::-webkit-calendar-picker-indicator {
  display: none;
  -webkit-appearance: none;
}
.wc-sheet-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 16px 8px 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  position: relative;
}
.wc-task-highlight {
  border: 1px solid #66bb6a;
  background: rgba(102, 187, 106, 0.05);
}
.wc-sheet-tile:active {
  transform: scale(0.95);
  background: rgba(255, 255, 255, 0.08);
}
@media (hover: hover) {
  .wc-sheet-tile:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: var(--tile-color, rgba(255, 255, 255, 0.1));
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  }
}
.wc-sheet-tile-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.35);
  flex-shrink: 0;
}
.wc-sheet-tile-title {
  font-size: 0.72rem;
  font-weight: 700;
  color: white;
  line-height: 1.25;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.wc-sheet-tile-meta {
  font-size: 0.6rem;
  color: rgba(255, 255, 255, 0.3);
  margin-top: 3px;
}

/* ===== ÄMTLI SMART-ASSIGNMENT DIALOG ===== */
.wc-task-time-picker {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.02);
  flex-shrink: 0;
}
.wc-task-time-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
}
.wc-task-time-input {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: white;
  font-size: 1.3rem;
  font-weight: 700;
  text-align: center;
  padding: 6px 12px;
  width: 110px;
  outline: none;
  -webkit-appearance: none;
  font-variant-numeric: tabular-nums;
}
.wc-task-time-input:focus {
  border-color: rgba(171, 71, 188, 0.5);
  box-shadow: 0 0 0 3px rgba(171, 71, 188, 0.15);
}
.wc-task-time-btn {
  background: rgba(255, 255, 255, 0.06) !important;
  color: rgba(255, 255, 255, 0.5) !important;
  border-radius: 10px !important;
}
.wc-task-time-btn:active {
  background: rgba(255, 255, 255, 0.12) !important;
}

/* Employee Availability Cards */
.wc-task-emp-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}
.wc-task-emp-free {
  border-color: rgba(76, 175, 80, 0.2);
}
.wc-task-emp-free:active {
  background: rgba(76, 175, 80, 0.1);
  transform: scale(0.98);
}
.wc-task-emp-busy {
  border-color: rgba(255, 152, 0, 0.15);
}
.wc-task-emp-busy:active {
  background: rgba(255, 152, 0, 0.08);
  transform: scale(0.98);
}
.wc-task-emp-noshift {
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;
}
.wc-task-emp-avatar {
  border: 2px solid rgba(255, 255, 255, 0.08) !important;
  flex-shrink: 0;
}
.wc-task-emp-info {
  flex: 1;
  min-width: 0;
}
.wc-task-emp-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: white;
}
.wc-task-emp-shift {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.35);
  display: flex;
  align-items: center;
  margin-top: 2px;
}
.wc-task-emp-status {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}
.wc-task-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
}
.wc-task-badge-free {
  background: rgba(76, 175, 80, 0.2);
  color: #66bb6a;
}
.wc-task-badge-busy {
  background: rgba(255, 152, 0, 0.15);
  color: #ffa726;
}
.wc-task-badge-off {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.6rem;
}
.wc-task-alt {
  font-size: 0.6rem;
  color: #66bb6a;
  font-weight: 600;
}

/* ===== PREMIUM HEADER ===== */
.wc-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: var(--bg-header);
  border-bottom: 1px solid var(--border-medium);
  z-index: 20;
}
/* Mobile: stack into two rows */
.wc-header-mobile {
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 12px;
}
.wc-header-mobile .wc-header-left {
  width: 100%;
}
.wc-header-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}
.wc-header-mobile .wc-header-controls {
  width: 100%;
  border-top: 1px solid var(--border-light);
  padding-top: 6px;
}
.wc-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.wc-back-btn {
  background: var(--bg-panel) !important;
  border-radius: 8px !important;
}
.wc-back-btn .v-icon {
  color: var(--text-main) !important;
}

.wc-date-block {
  display: flex;
  flex-direction: column;
}
.wc-day-name {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--text-main);
  line-height: 1.2;
  text-transform: capitalize;
}
.wc-full-date {
  font-size: 0.85rem;
  color: var(--text-muted);
  font-weight: 500;
}
.wc-today-badge {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 4px;
  background: linear-gradient(135deg, #4caf50, #2e7d32);
  color: white;
}
.wc-header-center {
  display: flex;
  align-items: center;
  gap: 16px;
}
.wc-stat {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-muted);
}
.wc-header-right {
  display: flex;
  align-items: center;
  gap: 4px;
}
.wc-nav-group {
  display: flex;
  align-items: center;
  background: var(--bg-panel);
  border-radius: 8px;
  padding: 2px;
  margin-right: 4px;
}
.wc-nav-btn {
  color: var(--icon-muted) !important;
}
.wc-nav-btn:hover {
  color: var(--text-main) !important;
}
.wc-today-btn {
  color: var(--text-muted) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.7rem !important;
  min-width: 0 !important;
  padding: 0 8px !important;
  border-radius: 6px !important;
}
.wc-today-btn.is-today {
  background: rgba(76, 175, 80, 0.15) !important;
  color: #66bb6a !important;
}
.wc-icon-btn {
  color: var(--icon-muted) !important;
  transition: all 0.2s;
}
.wc-icon-btn:hover,
.wc-icon-btn.active {
  color: var(--text-main) !important;
  background: var(--bg-panel-hover) !important;
}
.wc-view-toggle {
  background: rgba(76, 175, 80, 0.1) !important;
  color: #66bb6a !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.7rem !important;
  border-radius: 6px !important;
  padding: 0 10px !important;
  min-width: 0 !important;
  height: 26px !important;
  letter-spacing: 0;
  border: 1px solid rgba(76, 175, 80, 0.15) !important;
}
.wc-view-toggle:hover {
  background: rgba(76, 175, 80, 0.2) !important;
}

/* ===== EXISTING UTILITIES ===== */
.glass-panel,
.glass-btn,
.glass-panel-light {
  background: var(--bg-panel) !important;
  border: 1px solid var(--border-color) !important;
}
.glass-purple {
  background: linear-gradient(135deg, rgba(0, 150, 63, 0.15), rgba(0, 150, 63, 0.05));
  border: 1px solid rgba(0, 150, 63, 0.3);
}
.border-right {
  border-right: 1px solid var(--border-light);
}
.time-slot-label {
  position: relative;
  border-bottom: 1px solid var(--bg-time-line);
}

.time-slot-label {
  /* border-bottom entfernt, da es die 1px Verschiebung verursacht hat! */
  position: relative;
}
.wc-hour-label {
  position: absolute;
  top: 0;
  transform: translateY(-50%); /* Zentriert die Zahl millimetergenau auf der Linie */
  left: 6px;
  right: auto;
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-disabled);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.wc-hour-tick {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--bg-time-line); /* Ersetzt den alten Rahmen, ohne die Höhe zu beeinflussen */
}

/* ===== DAS NEUE CURRENT TIME DESIGN ===== */
.time-axis-current-badge {
  position: absolute;
  right: -1px; /* Liegt perfekt an der Begrenzungslinie an */
  transform: translateY(-50%); /* Exakt auf der Linie zentriert */
  background: #ff5252;
  color: white;
  padding: 3px 5px;
  border-radius: 4px 0 0 4px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 6px rgba(255, 82, 82, 0.4);
}

.current-time-line {
  position: absolute;
  height: 2px;
  background: #ff5252;
  pointer-events: none;
  z-index: 20;
  box-shadow: 0 0 6px rgba(255, 82, 82, 0.4);
}

/* Der kleine Leucht-Punkt links am Beginn der roten Linie */
.current-time-line::after {
  content: "";
  position: absolute;
  left: 0;
  top: -3px;
  width: 8px;
  height: 8px;
  background: #ff5252;
  border-radius: 50%;
  box-shadow: 0 0 4px rgba(255, 82, 82, 0.8);
}
/* ===== SCROLLBAR ===== */
.custom-scrollbar::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: var(--border-medium);
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: var(--border-strong);
}

/* Scroller for columns */
.wc-scroller {
  display: flex;
  flex: 1 1 auto;
  overflow: auto;
  position: relative;
  -webkit-overflow-scrolling: touch;
  isolation: isolate; /* Eigener Stacking-Context: z-index Werte bleiben im Scroller und laufen nicht über Sidebar/Overlays */
}

/* iPad & Touch */
@media (max-width: 1024px) {
  .wc-header {
    padding: 8px 12px;
  }
  .wc-day-name {
    font-size: 1.2rem;
  }
}

@media (hover: none) and (pointer: coarse) {
  .wc-scroller {
    -webkit-overflow-scrolling: touch;
  }
  .employee-column {
    min-width: 200px !important;
  }
}

/* Template Dialog */
.wc-color-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  cursor: pointer;
  opacity: 0.5;
  border: 2px solid transparent;
  transition: all 0.15s;
}
.wc-color-dot:hover {
  opacity: 0.8;
  transform: scale(1.1);
}
.wc-color-dot.active {
  opacity: 1;
  transform: scale(1.2);
  border-color: white;
  box-shadow: 0 0 10px currentColor;
}
.wc-icon-pick {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  background: var(--bg-panel);
  border: 1px solid transparent;
  transition: all 0.15s;
}
.wc-icon-pick:hover {
  background: var(--bg-panel-hover);
}
.wc-icon-pick.active {
  background: rgba(76, 175, 80, 0.15);
  border-color: #4caf50;
}

/* ===== LOADING SKELETON ===== */
.wc-skeleton-col {
  min-width: 240px;
  flex: 1 1 240px;
  border-right: 1px solid rgba(255, 255, 255, 0.04);
  display: flex;
  flex-direction: column;
}
.wc-skeleton-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: var(--bg-header);
  border-bottom: 1px solid var(--border-medium);
}
.wc-skeleton-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
}
.wc-skeleton-line {
  border-radius: 4px;
}
.wc-skeleton-body {
  position: relative;
  height: 600px;
}
.wc-skeleton-block {
  position: absolute;
  left: 8px;
  right: 8px;
  border-radius: 10px;
}

/* Pulse Animation */
.wc-pulse {
  background: var(--bg-skeleton);
  animation: wcPulse 1.5s ease-in-out infinite;
}
@keyframes wcPulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

/* Empty State */
.wc-empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: var(--bg-panel);
  border: 1px dashed var(--border-strong);
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
